import { useGLTF, meshBounds } from "@react-three/drei";
import { useEffect, useRef, Suspense, useState } from "react";
import * as THREE from 'three';
import { useFrame } from "@react-three/fiber";
import { ANIMATION_SPEED, WHEEL_SPEED_MULTIPLIER } from '../constants';

function CarModel({ isMoving, fadeSpeed, isEngineOn, soundGraphics }) {
    const { scene } = useGLTF('/mzd.glb');
    const wheelRefs = useRef([]);
    const exhaustRef = useRef();
    const exhaustGroupRef = useRef(new THREE.Group());
    const originalExhaustPosition = useRef(new THREE.Vector3());
    const originalExhaustRotation = useRef(new THREE.Euler());
    const carRef = useRef();
    const [opacity, setOpacity] = useState(0);

    useEffect(() => {
        if (!scene) {
            console.error('Model failed to load');
            return;
        }

        scene.scale.set(0.5, 0.5, 0.5);
        scene.position.set(0, -0.9, -2);

        scene.traverse((child) => {
            if (child.isMesh) {
                child.raycast = meshBounds;
                
                if (child.geometry) {
                    child.geometry.computeBoundingSphere();
                    child.geometry.computeBoundingBox();
                }

                if (child.material && child.name !== 'Cylinder_Chrome_Parts_0') {
                    child.material.side = THREE.FrontSide;
                    child.material.shadowSide = THREE.BackSide;
                    child.material.needsUpdate = true;
                    child.material.transparent = true;
                    child.material.opacity = 0;
                }

                child.castShadow = true;
                child.receiveShadow = false;

                if (child.name.toLowerCase().includes('wheel')) {
                    wheelRefs.current.push(child);
                }

                // Store reference to exhaust pipe and set its material
                if (child.name === 'Cylinder_Chrome_Parts_0') {
                    exhaustRef.current = child;
                    
                    // Get the world position and rotation of the exhaust pipe
                    const worldPosition = new THREE.Vector3();
                    const worldQuaternion = new THREE.Quaternion();
                    const worldScale = new THREE.Vector3();
                    
                    child.updateWorldMatrix(true, false);
                    child.matrixWorld.decompose(worldPosition, worldQuaternion, worldScale);
                    
                    // Remove from parent
                    child.removeFromParent();
                    
                    // Store original values
                    originalExhaustPosition.current.copy(worldPosition);
                    originalExhaustRotation.current.setFromQuaternion(worldQuaternion);
                    
                    // Add to the group
                    exhaustGroupRef.current.position.copy(worldPosition);
                    exhaustGroupRef.current.quaternion.copy(worldQuaternion);
                    exhaustGroupRef.current.scale.copy(worldScale);
                    exhaustGroupRef.current.add(child);
                }
            }
        });

        scene.castShadow = true;
    }, [scene]);

    useFrame((state, delta) => {
        if (isMoving) {
            wheelRefs.current.forEach(wheel => {
                wheel.rotation.x += delta * ANIMATION_SPEED * WHEEL_SPEED_MULTIPLIER;
            });
        }

        // Animate exhaust pipe based on sound
        if (exhaustGroupRef.current && isEngineOn && soundGraphics) {
            const soundData = soundGraphics.getAllFrequencyData();
            
            // Get intensities from sound
            const bassIntensity = Math.pow(soundData.bass.average / 255, 2); // Квадратичная зависимость для басов
            const volumeIntensity = Math.pow(soundData.volume / 128, 2); // Квадратичная зависимость для громкости
            
            // Calculate shake intensity with exponential increase for high values
            const baseShakeIntensity = (bassIntensity * 0.8 + volumeIntensity * 0.2);
            const shakeIntensity = baseShakeIntensity < 0.5 
                ? baseShakeIntensity * 0.015  // Уменьшенная умеренная тряска
                : baseShakeIntensity * 0.06;   // Уменьшенная сильная тряска
            
            // Get current time for smooth animation
            const time = state.clock.getElapsedTime();
            
            // Rotate based on sound intensity with increased effect at high values
            exhaustGroupRef.current.rotation.x = originalExhaustRotation.current.x + 
                Math.sin(time * 30) * shakeIntensity;
            exhaustGroupRef.current.rotation.y = originalExhaustRotation.current.y + 
                Math.cos(time * 25) * (volumeIntensity * 0.02);
            exhaustGroupRef.current.rotation.z = originalExhaustRotation.current.z + 
                Math.sin(time * 20) * (bassIntensity * 0.03);
            
            // Add position vibration with increased effect at high values
            const positionIntensity = shakeIntensity * (baseShakeIntensity > 0.7 ? 0.01 : 0.003);
            exhaustGroupRef.current.position.x = originalExhaustPosition.current.x + 
                Math.sin(time * 35) * positionIntensity;
            exhaustGroupRef.current.position.y = originalExhaustPosition.current.y + 
                Math.cos(time * 40) * positionIntensity;
            exhaustGroupRef.current.position.z = originalExhaustPosition.current.z + 
                Math.sin(time * 45) * positionIntensity;

        } else if (exhaustGroupRef.current) {
            // Reset to original position and rotation when engine is off
            exhaustGroupRef.current.position.copy(originalExhaustPosition.current);
            exhaustGroupRef.current.rotation.copy(originalExhaustRotation.current);
        }

        if (opacity < 1) {
            const newOpacity = Math.min(1, opacity + delta * fadeSpeed);
            setOpacity(newOpacity);
            scene.traverse((child) => {
                if (child.isMesh && child.material && child.name !== 'Cylinder_Chrome_Parts_0') {
                    child.material.opacity = newOpacity;
                }
            });
        }
    });

    if (!scene) {
        return null;
    }

    return (
        <group>
            <primitive ref={carRef} object={scene} castShadow />
            <group ref={exhaustGroupRef} />
        </group>
    );
}

// Preload the model
useGLTF.preload('/mzd.glb');

export default function Car({ isMoving, fadeSpeed, isEngineOn, soundGraphics }) {
    return (
        <Suspense fallback={null}>
            <CarModel isMoving={isMoving} fadeSpeed={fadeSpeed} isEngineOn={isEngineOn} soundGraphics={soundGraphics} />
        </Suspense>
    );
}